import { Build } from "app/components/build/Show/lib/types";
import UserAvatar, { User } from "app/components/shared/UserAvatar";
import { Outcome, State } from "app/lib/pipeline";
import { Node, NodeProps } from "@xyflow/react";
import * as HoverCard from "./StepHoverCard";
import Button from "app/components/shared/Button";
import BuildStatusDescription from "app/components/shared/BuildStatusDescription";
import BuildTriggerLabel from "app/components/shared/BuildTriggerLabel";
import BuildMessage from "app/components/build/Message";
import { shortCommit } from "app/lib/commits";
import BaseNode from "./BaseNode";
import { useCallback } from "react";
import Icon from "app/components/shared/Icon";
import { twMerge } from "tailwind-merge";

type BuildNodeType = Node<Build & Record<string, unknown>, "build">;

export const BuildNode = ({ data: build, ...props }: NodeProps<BuildNodeType>) => {
  const user: User = {
    name: build.authorName,
    avatar: { url: build.authorAvatar || "" },
  };

  const buildMessageSecondLine = build.message?.split("\n")[2] || "";

  const handleCommitClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <HoverCard.Root>
      <HoverCard.Portal>
        <HoverCard.Content {...props} noBorder={true} state={State.Ready} outcome={Outcome.Neutral}>
          <div className="flex flex-col items-start w-auto divide-y">
            <div className="flex flex-row gap-1 items-center">
              <div className="self-start">
                <CommitProviderIcon build={build} />
              </div>
              <div className="flex flex-col w-full">
                <BuildMessage message={build.message} url={build.commitUrl || ""} />
                {buildMessageSecondLine && buildMessageSecondLine.length > 0 && (
                  <span className="max-w-xs mx-auto w-full overflow-hidden text-ellipsis line-clamp-3 break-words break-normal dark-gray">
                    {buildMessageSecondLine}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col flex-1 w-full gap-2">
              <div className="flex flex-row items-center gap-2 w-full">
                <UserAvatar user={user} className="h-10 w-10" />

                <div className="flex flex-col flex-nowrap flex-1">
                  <div className="flex flex-row flex-nowrap items-center justify-center gap-1">
                    <span className="font-medium">{user.name}</span>
                    <span className="flex items-center gap-2 ml-auto dark-gray build-secondary-time">
                      <BuildTriggerLabel source={build.source} />
                    </span>
                  </div>
                  <span className="flex items-center gap-2 dark-gray">
                    <BuildStatusDescription build={build} />
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex flex-row flex-nowrap gap-1">
                  <span className="font-medium">Build:</span>
                  <a
                    className="color-inherit hover-color-inherit"
                    href={build.path}
                    target="_blank"
                    rel="noreferrer"
                  >
                    #{build.number}
                  </a>
                </div>
                <div className="flex flex-row flex-nowrap gap-1">
                  <span className="font-medium">Branch:</span>
                  <a
                    className="color-inherit hover-color-inherit"
                    href={build.branchPath}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {build.branchName}
                  </a>
                </div>
                <div className="flex flex-row flex-nowrap gap-1">
                  <span className="font-medium">Commit:</span>
                  <a
                    href={build.commitUrl || ""}
                    className="color-inherit hover-color-inherit"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {shortCommit(build.commitId, build.commitShortLength)}
                  </a>
                </div>
              </div>
              <div className="flex flex-row justify-between pt-4">
                <Button
                  href={build.commitUrl || ""}
                  theme="small"
                  target="_blank"
                  onClick={handleCommitClick}
                >
                  View Commit
                </Button>
              </div>
            </div>
          </div>
        </HoverCard.Content>
      </HoverCard.Portal>
      <HoverCard.Trigger asChild={true}>
        <div>
          <BaseNode
            {...props}
            hideStateIcon={true}
            data={{ state: State.Finished, outcome: Outcome.Passed }}
            className="rounded-full p-1"
            icon={<UserAvatar user={user} className="h-10 w-10" />}
          />
        </div>
      </HoverCard.Trigger>
    </HoverCard.Root>
  );
};

const CommitProviderIcon = ({ build }) => {
  const icon = build.project.provider.frontendIcon;
  if (build.commitId) {
    return (
      <Icon
        icon={`custom/solid/provider-${icon}`}
        className={twMerge("h-9 w-9 mr-1 text-current", icon === "bitbucket" && "text-[#205081]")}
      />
    );
  }

  return null;
};
